import React from "react";

interface CohortRow {
  vintage: string;
  loans: string;
  balance: string;
  m12: string;
  m24: string;
  m36: string;
  m48: string;
  m60: string;
  risk: "low" | "mid" | "high";
}

export const CohortMatrix: React.FC = () => {
  const cohortData: CohortRow[] = [
    {
      vintage: "2025 Q1",
      loans: "2,384",
      balance: "$1.13B",
      m12: "0.6%",
      m24: "1.3%",
      m36: "2.1%",
      m48: "2.8%",
      m60: "3.2%",
      risk: "low",
    },
    {
      vintage: "2024 Q4",
      loans: "1,947",
      balance: "$0.92B",
      m12: "0.7%",
      m24: "1.5%",
      m36: "2.4%",
      m48: "3.1%",
      m60: "3.5%",
      risk: "low",
    },
    {
      vintage: "2024 Q3",
      loans: "1,763",
      balance: "$0.86B",
      m12: "0.9%",
      m24: "1.9%",
      m36: "2.9%",
      m48: "3.7%",
      m60: "4.1%",
      risk: "mid",
    },
    {
      vintage: "2024 Q2",
      loans: "1,502",
      balance: "$0.72B",
      m12: "1.0%",
      m24: "2.2%",
      m36: "3.3%",
      m48: "4.2%",
      m60: "4.8%",
      risk: "mid",
    },
    {
      vintage: "2023 Q4",
      loans: "1,289",
      balance: "$0.61B",
      m12: "1.4%",
      m24: "2.8%",
      m36: "4.1%",
      m48: "5.3%",
      m60: "6.0%",
      risk: "high",
    },
    {
      vintage: "2023 Q3",
      loans: "1,154",
      balance: "$0.57B",
      m12: "1.5%",
      m24: "3.0%",
      m36: "4.5%",
      m48: "5.7%",
      m60: "6.5%",
      risk: "high",
    },
  ];

  return (
    <article className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Cohort matrix · 60M window
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Each row is an origination vintage; columns show cumulative defaults
            by months-on-book.
          </div>
        </div>
      </div>

      <div className="rounded-xl overflow-auto border border-[rgba(139,146,176,0.35)] bg-gradient-to-b from-[#1c2243] to-[#050716] max-h-[360px]">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-[1] bg-[rgba(5,7,22,0.95)] backdrop-blur-sm">
            <tr>
              {[
                "Vintage",
                "Loans",
                "Balance",
                "12M",
                "24M",
                "36M",
                "48M",
                "60M",
                "Risk",
              ].map((header) => (
                <th
                  key={header}
                  className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] whitespace-nowrap"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohortData.map((cohort) => (
              <tr
                key={cohort.vintage}
                className="hover:bg-[rgba(0,212,255,0.08)] transition-colors"
              >
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.vintage}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.loans}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.balance}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.m12}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.m24}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.m36}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.m48}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {cohort.m60}
                </td>
                <td className="px-3.5 py-3 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.12em] ${
                      cohort.risk === "low"
                        ? "bg-[rgba(0,255,136,0.18)] text-neon-green border border-[rgba(0,255,136,0.7)]"
                        : cohort.risk === "mid"
                        ? "bg-[rgba(255,184,0,0.18)] text-warning-amber border border-[rgba(255,184,0,0.7)]"
                        : "bg-[rgba(255,51,102,0.18)] text-error-red border border-[rgba(255,51,102,0.7)]"
                    }`}
                  >
                    {cohort.risk === "low"
                      ? "Low"
                      : cohort.risk === "mid"
                      ? "Medium"
                      : "High"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-1.5 text-[11px] text-[#8b92b0] flex items-center justify-between gap-2 flex-wrap">
        <span>
          Showing <strong>6</strong> vintages · Defaults are AI-analyzed
          for participating partners.
        </span>
        <span>Use API for full cohort cube · v2.0 schema</span>
      </div>
    </article>
  );
};
